import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import {
  Box,
  ClickAwayListener,
  List,
  ScopedCssBaseline,
  Stack,
  styled,
  Theme,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import { withRouter, WithRouterProps } from 'react-router';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faGear } from '@fortawesome/pro-solid-svg-icons';
import { CustomListItemText } from 'new/components/AppBar/Sidebar';
import { CustomListItem } from 'new/components/AppBar/styles';
import {
  Question,
  Settings,
} from 'new/components/AppBar/SidebarPopoverContent';
import { permissionsLabelMap } from 'new/modules/OfficeSettings/components/OfficeSettingsUsers';
import { PermissionType } from 'new/modules/OfficeSettings/types';
import useActiveOffice from 'new/hooks/useActiveOffice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LeaderboardIcon from 'new/modules/SeasonOfGiving/Leaderboard/interoffice/components/LeaderboardIcon';
import { TenantStruct } from 'new/types/util';
import { TENANT } from 'new/tenants';

const getBottomActionButtonStyles = (
  theme: Theme,
): TenantStruct<Record<string, string>> => ({
  dhq: {
    color: `${theme.palette.grey[600]}!important`,
  },
  vhq: {},
});

const getWelcomeStyles = (
  theme: Theme,
): TenantStruct<Record<string, string>> => ({
  dhq: {
    color: theme.palette.primary.dark,
  },
  vhq: {
    color: theme.palette.primary.main,
  },
});

const BottomContainerBox = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  color: '#fff',

  '.welcomeUser': {
    textAlign: 'center',
    color: '#fff',
    fontSize: '0.875rem',
  },

  '.isActionButtonActive': {
    '.MuiListItemText-root .MuiTypography-root': {
      color: theme.palette.warning.light,
    },
    svg: {
      color: theme.palette.warning.light,
    },
    '&.isSuperAdminSidebar': {
      '.MuiListItemText-root .MuiTypography-root': {
        color: `${theme.palette.dhqLighterBlue.light}!important`,
      },
      svg: {
        color: `${theme.palette.dhqLighterBlue.light}!important`,
      },
    },
  },

  '&.isSuperAdminSidebar': {
    '.bottomActionButton': {
      '.MuiTypography-root, .listItemIcon': {
        ...getBottomActionButtonStyles(theme)[TENANT.id],
      },
      '&:hover': {
        backgroundColor: theme.palette.common.white,
        '.MuiTypography-root, .listItemIcon': {
          color: `${theme.palette.grey[800]}!important`,
        },
      },
    },
    '.isActionButtonActive': {
      '.MuiListItemText-root .MuiTypography-root': {
        color: `${theme.palette.dhqLighterBlue.light}!important`,
      },
      svg: {
        color: `${theme.palette.dhqLighterBlue.light}!important`,
      },
    },
    '.welcomeUser': {
      ...getWelcomeStyles(theme)[TENANT.id],
    },
  },

  '&.collapsed': {
    '.welcomeUser': {
      visibility: 'hidden',
    },
  },

  '.navbarBottomIcon': {
    ':hover': {
      color: 'yellow',
    },
  },
}));

const CustomTooltip = styled(
  ({ className, children, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }}>
      {children}
    </Tooltip>
  ),
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.contrastText,
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.161)',
    padding: theme.spacing(1),

    '.MuiTooltip-arrow': {
      color: theme.palette.primary.contrastText,
    },
  },
}));

const SidebarActionsTooltip = styled(
  ({ className, children, ...props }: TooltipProps) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [20, 0],
            },
          },
        ],
      }}
    >
      {children}
    </Tooltip>
  ),
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.contrastText,
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.161)',
    padding: theme.spacing(3),
  },
  '.MuiTooltip-arrow': {
    color: theme.palette.primary.contrastText,
    left: `-20px !important`,
  },
}));

const CustomTooltipContent = styled(Box)(({ theme }) => ({
  p: {
    fontSize: '.875rem',
    color: theme.palette.grey[800],
  },
}));

type Props = {
  isExpanded: boolean;
  isHovered: boolean;
  isSuperAdminSidebar?: boolean;
};

const NavbarBottomPart: React.FC<Props & WithRouterProps> = ({
  isExpanded,
  isHovered,
  location,
  isSuperAdminSidebar = false,
}) => {
  const [activeButtonIndex, setActiveButtonIndex] = useState<number>();
  const { isPatient, isLoadingUser } = useActiveOffice();

  useEffect(() => {
    setActiveButtonIndex(undefined);
  }, [location.pathname]);

  useEffect(() => {
    if (!isHovered) {
      setActiveButtonIndex(undefined);
    }
  }, [isHovered]);

  const PrivacyLinks = (
    <div>
      <a href="/privacy" target="_blank">
        Privacy Policy
      </a>
      <br />
      <a href="/terms" target="_blank">
        Terms & Conditions
      </a>
    </div>
  );

  let menuItems = [
    {
      icon: faCircleQuestion,
      component: PrivacyLinks,
      label: 'Policies',
      isHidden: !isPatient,
    },
    {
      icon: faGear,
      component: <Settings />,
      label: 'Settings',
    },
    {
      icon: faCircleQuestion,
      component: <Question />,
      label: 'Help',
      isHidden: isPatient,
    },
  ];

  const { user: data } = useActiveOffice();
  const userTypeLabel =
    data?.data.type &&
    permissionsLabelMap[data?.data.type as PermissionType]?.toUpperCase();
  const upgradedOffice = data?.data.tieredPayment?.tierLevel != 0;
  if (!upgradedOffice) menuItems = [];

  return (
    <BottomContainerBox
      className={cx({
        collapsed: !isExpanded && !isHovered,
        isSuperAdminSidebar,
      })}
    >
      <Stack direction="row" justifyContent="center" alignItems="center">
        {userTypeLabel && <LeaderboardIcon />}
        <CustomTooltip
          title={
            <CustomTooltipContent p={1}>
              <Typography>{data?.data?.email}</Typography>
              {userTypeLabel && <Typography>{userTypeLabel} user</Typography>}
              {userTypeLabel && (
                <Typography>
                  <b>Personal Referral Code: </b>
                  <span id="referralCode">{data?.data?.id}</span>
                </Typography>
              )}
            </CustomTooltipContent>
          }
          placement="top"
          arrow
        >
          <Box className="welcomeUser">
            Welcome,
            <br />
            <strong>
              {data?.data.firstName} {data?.data.lastName}
            </strong>
          </Box>
        </CustomTooltip>
      </Stack>
      <Box mt={2} mb={2}>
        <List>
          {menuItems.map((item, index) => {
            if (isLoadingUser || item.isHidden) return;

            return (
              <ClickAwayListener
                key={index}
                onClickAway={(e) => {
                  const isActionBtnClicked = e.composedPath().some((node) => {
                    const { className } = node as HTMLElement;

                    if (typeof className === 'string') {
                      return (
                        className?.includes('bottomActionButton') ||
                        className?.includes('bottomActionTooltip')
                      );
                    }

                    return false;
                  });

                  if (!isActionBtnClicked) {
                    setActiveButtonIndex(undefined);
                  }
                }}
              >
                <div>
                  <SidebarActionsTooltip
                    open={index === activeButtonIndex}
                    title={
                      <div className="bottomActionTooltip">
                        <ScopedCssBaseline>{item.component}</ScopedCssBaseline>
                      </div>
                    }
                    placement="top-end"
                    arrow
                  >
                    <CustomListItem
                      className={cx('bottomActionButton', {
                        isActionButtonActive: activeButtonIndex === index,
                        isBottom: true,
                        isSuperAdmin: isSuperAdminSidebar,
                      })}
                      onClick={() => {
                        if (isExpanded || isHovered) {
                          setActiveButtonIndex((prevIndex) =>
                            prevIndex === index ? undefined : index,
                          );
                        }
                      }}
                    >
                      <Box className="listItemIcon" sx={{ margin: '2px 0' }}>
                        <FontAwesomeIcon icon={item.icon as IconProp} />
                      </Box>
                      <CustomListItemText
                        primary={item.label}
                        className={isExpanded || isHovered ? '' : 'collapsed'}
                      />
                    </CustomListItem>
                  </SidebarActionsTooltip>
                </div>
              </ClickAwayListener>
            );
          })}
        </List>
      </Box>
    </BottomContainerBox>
  );
};

export default withRouter(NavbarBottomPart);
